import {
  Integration,
  IntegrationConnection,
  IntegrationConnectionConfig,
  ToolSetConfigurationSchema,
  ToolSetType,
  UpdateIntegrationConnectionConfigRequest,
} from "@cohesive/api-client";
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query";
import { AxiosError } from "axios";
import { kebabCase } from "change-case";
import { orgAPI } from "./api";

export interface ToolSpecSearchResult {
  name: string;
  display_name: string;
  description: string;
}

export function useDeleteOrgIntegrationConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      integrationConnection,
      integration,
    }: {
      integrationConnection: IntegrationConnection;
      integration: Integration;
    }) => {
      if (integration.auth_level === "organization") {
        await orgAPI.integrationConnections.delete(integrationConnection.id);
      } else if (integration.auth_level === "user") {
        await orgAPI.integrationConnections.deleteUserIntegrationConnection(
          integrationConnection.id,
        );
      } else {
        // Fallback to original endpoint for backward compatibility
        await orgAPI.integrationConnections.delete(integrationConnection.id);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integrationConnections"],
      });
    },
  });
}

export type IntegrationEnhanced = Integration & {
  slug: string;
  connected: boolean;
  integration_connection_id?: string;
  integration_connection_config?: IntegrationConnectionConfig;
  tool_set_id: string;
  type: ToolSetType;
  integration_provider_oauth_app_id?: string;
  configuration_schema?: ToolSetConfigurationSchema;
  /**
   * The authenticated CLI binary the agent runs for this toolset (e.g.
   * "slack-mcp-cli"), or absent for non-CLI-backed toolsets. When present, the
   * connector detail page renders an informational/capability view instead of
   * per-tool/per-trigger cards.
   */
  cli_name?: string;
};

export function useOrgIntegrations(
  options?: UseQueryOptions<IntegrationEnhanced[], AxiosError>,
) {
  return useQuery<IntegrationEnhanced[], AxiosError>({
    queryKey: ["orgIntegrations"],
    queryFn: async () => {
      const toolsets = await orgAPI.toolSets.userList({
        limit: 10000,
      });
      const integrations = toolsets.data.tool_sets.flatMap((toolset) => {
        const integration: IntegrationEnhanced = {
          id: toolset.integration_id!,
          name: toolset.display_name,
          description: toolset.description,
          platform: toolset.integration?.platform!,
          status: toolset.status,
          starred: toolset.starred,
          provider: toolset.integration_provider!,
          provider_id: toolset.integration_provider_id!,
          integration_provider_oauth_app_id:
            toolset.integration_provider_oauth_app_id,
          auth_level: toolset.integration?.auth_level as
            | "user"
            | "organization",
          auth_method: toolset.integration?.auth_method as "oauth2",
          metadata: {
            image_url: toolset.integration?.metadata?.image_url,
            weight: toolset.integration?.metadata?.weight,
            categories: toolset.integration?.metadata?.categories,
            mcp: toolset.integration?.metadata?.mcp,
          },
          created_at: toolset.created_at,
          updated_at: toolset.updated_at,
          slug: "",
          connected: !!toolset.integration_connection,
          integration_connection_id: toolset.integration_connection?.id,
          integration_connection_config: toolset.integration_connection?.config,
          tool_set_id: toolset.id,
          type: toolset.type,
          configuration_schema: toolset.configuration_schema,
          cli_name: toolset.cli_name,
        };

        return integration;
      });

      const filteredIntegrations = integrations.filter((integration) => {
        if (integration.type === "system" || integration.type === "internal") {
          return false;
        }

        return true;
      });

      // Track slug counts to handle duplicates
      const slugCounts = new Map<string, number>();

      return filteredIntegrations.map((integration) => {
        const baseSlug = kebabCase(integration.name, {});
        const currentCount = slugCounts.get(baseSlug) || 0;
        slugCounts.set(baseSlug, currentCount + 1);

        const slug =
          currentCount === 0 ? baseSlug : `${baseSlug}-${currentCount + 1}`;

        return {
          ...integration,
          slug,
        };
      });
    },
    ...options,
  });
}

export function useDiscoverableOrgIntegrations(
  options?: UseQueryOptions<IntegrationEnhanced[], AxiosError>,
) {
  return useQuery<IntegrationEnhanced[], AxiosError>({
    queryKey: ["discoverableOrgIntegrations"],
    queryFn: async () => {
      const toolsets = await orgAPI.toolSets.userList({
        limit: 10000,
        discoverable: true,
      });
      return toolsets.data.tool_sets.flatMap((toolset) => {
        const integration: IntegrationEnhanced = {
          id: toolset.integration_id!,
          name: toolset.display_name,
          description: toolset.description,
          platform: toolset.integration?.platform!,
          status: toolset.status,
          starred: toolset.starred,
          provider: toolset.integration_provider!,
          provider_id: toolset.integration_provider_id!,
          integration_provider_oauth_app_id:
            toolset.integration_provider_oauth_app_id,
          auth_level: toolset.integration?.auth_level as
            | "user"
            | "organization",
          auth_method: toolset.integration?.auth_method as "oauth2",
          metadata: {
            image_url: toolset.integration?.metadata?.image_url,
            weight: toolset.integration?.metadata?.weight,
            categories: toolset.integration?.metadata?.categories,
            mcp: toolset.integration?.metadata?.mcp,
          },
          created_at: toolset.created_at,
          updated_at: toolset.updated_at,
          slug: "",
          connected: !!toolset.integration_connection,
          integration_connection_id: toolset.integration_connection?.id,
          integration_connection_config: toolset.integration_connection?.config,
          tool_set_id: toolset.id,
          type: toolset.type,
          configuration_schema: toolset.configuration_schema,
          cli_name: toolset.cli_name,
        };

        return integration;
      });
    },
    ...options,
  });
}

export type IntegrationConnectionWithSlug = IntegrationConnection & {
  slug: string;
};

export function useOrgIntegrationConnections(
  options?: Partial<
    UseQueryOptions<IntegrationConnectionWithSlug[], AxiosError>
  >,
) {
  return useQuery<IntegrationConnectionWithSlug[], AxiosError>({
    queryKey: ["integrationConnections"],
    queryFn: async () => {
      const integrations = await orgAPI.integrationConnections.list();

      // Track slug counts to handle duplicates
      const slugCounts = new Map<string, number>();

      return integrations.map((integration) => {
        const baseSlug = kebabCase(integration.integration_name);
        const currentCount = slugCounts.get(baseSlug) || 0;
        slugCounts.set(baseSlug, currentCount + 1);

        const slug =
          currentCount === 0 ? baseSlug : `${baseSlug}-${currentCount + 1}`;

        return {
          ...integration,
          slug,
        };
      });
    },
    ...options,
  });
}

export function useCreateIntegrationConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      provider,
      providerId,
      createdAt,
      integration,
    }: {
      provider: string;
      providerId: string;
      createdAt: string;
      integration: Integration;
    }) => {
      const data = {
        integration_id: integration.id,
        provider: provider,
        provider_id: providerId,
        created_at: createdAt,
      };

      if (integration.auth_level === "organization") {
        return await orgAPI.integrationConnections.create(data);
      } else if (integration.auth_level === "user") {
        return await orgAPI.integrationConnections.createUserIntegrationConnection(
          data,
        );
      } else {
        // Fallback to original endpoint for backward compatibility
        return await orgAPI.integrationConnections.create(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integrationConnections"],
      });
    },
  });
}

// =============================================================================
// Integration Connection Config Hooks
// =============================================================================

/**
 * Update connection config.
 * For org-level auth: uses the org endpoint (admin action).
 * For user-level auth: uses the user endpoint (owner action).
 */
export function useUpdateConnectionConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      integrationConnectionId,
      config,
      authLevel = "organization",
    }: {
      integrationConnectionId: string;
      config: UpdateIntegrationConnectionConfigRequest;
      authLevel?: "organization" | "user";
    }) => {
      if (authLevel === "user") {
        return await orgAPI.integrationConnections.updateUserConnectionConfig(
          integrationConnectionId,
          config,
        );
      }
      return await orgAPI.integrationConnections.updateConnectionConfig(
        integrationConnectionId,
        config,
      );
    },
    onSuccess: (data, { integrationConnectionId }) => {
      if (data && integrationConnectionId) {
        queryClient.invalidateQueries({
          queryKey: ["integrationConnections"],
          refetchType: "all",
        });
        queryClient.invalidateQueries({
          queryKey: ["orgIntegrations"],
          refetchType: "all",
        });
      }
    },
  });
}

/**
 * Update user's own config for an integration connection.
 * Only updates the user_settings entry for the current user.
 */
export function useUpdateUserConnectionConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      integrationConnectionId,
      config,
    }: {
      integrationConnectionId: string;
      config: UpdateIntegrationConnectionConfigRequest;
    }) => {
      return await orgAPI.integrationConnections.updateUserConnectionConfig(
        integrationConnectionId,
        config,
      );
    },
    onSuccess: (data, { integrationConnectionId }) => {
      if (data && integrationConnectionId) {
        queryClient.invalidateQueries({
          queryKey: ["integrationConnections"],
          refetchType: "all",
        });
        queryClient.invalidateQueries({
          queryKey: ["orgIntegrations"],
          refetchType: "all",
        });
      }
    },
  });
}

/**
 * Helper to get user's config from a connection
 */
export function getUserConfig(
  connection: IntegrationConnection | undefined,
  userId: string | undefined,
): Record<string, any> | undefined {
  if (!connection?.config?.user_settings?.user_mapping || !userId)
    return undefined;
  return connection.config.user_settings.user_mapping[userId];
}

/**
 * Helper to get shared settings from a connection
 */
export function getConnectionSettings(
  connection: IntegrationConnection | undefined,
): Record<string, any> | undefined {
  return connection?.config?.settings;
}

export function useUpdateIntegrationConnection() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      integrationConnectionId,
      instructions,
      integration,
    }: {
      integrationConnectionId: string;
      instructions: string;
      integration: Integration;
    }) => {
      if (integration.auth_level === "organization") {
        return await orgAPI.integrationConnections.update(
          integrationConnectionId,
          { instructions },
        );
      } else if (integration.auth_level === "user") {
        return await orgAPI.integrationConnections.updateUserIntegrationConnection(
          integrationConnectionId,
          { instructions },
        );
      } else {
        // Fallback to original endpoint for backward compatibility
        return await orgAPI.integrationConnections.update(
          integrationConnectionId,
          { instructions },
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["integrationConnections"],
      });
    },
  });
}

export function useToolSpecsWithSearch(
  searchTerm: string,
  options?: Partial<UseQueryOptions<ToolSpecSearchResult[], AxiosError>>,
) {
  return useQuery<ToolSpecSearchResult[], AxiosError>({
    queryKey: ["toolSpecsWithSearch", searchTerm],
    queryFn: () => orgAPI.toolSpec.search(searchTerm),
    // Always enabled - empty search term will return initial results
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
}
